import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import { useTranslation } from "react-i18next";

import { Container } from "../components/Container";
import { SectionHeading } from "../components/SectionHeading";
import { useLocale } from "../hooks/useLocale";
import { getMapPlaces, type MapPlacePin } from "../lib/api";

import "leaflet/dist/leaflet.css";

type MapCountryKey =
  | "albania"
  | "kosovo"
  | "montenegro"
  | "north_macedonia";

type MapCountry = {
  id: number;
  key: MapCountryKey;
  /** [southWest, northEast] so the view stays inside this country. */
  bounds: [[number, number], [number, number]];
};

const MAP_COUNTRIES: ReadonlyArray<MapCountry> = [
  {
    id: 2,
    key: "albania",
    bounds: [
      [39.64, 19.26],
      [42.67, 21.06],
    ],
  },
  {
    id: 193,
    key: "kosovo",
    bounds: [
      [41.85, 20.01],
      [43.27, 21.79],
    ],
  },
  {
    id: 114,
    key: "montenegro",
    bounds: [
      [41.85, 18.43],
      [43.56, 20.36],
    ],
  },
  {
    id: 127,
    key: "north_macedonia",
    bounds: [
      [40.85, 20.45],
      [42.37, 23.04],
    ],
  },
];

const OSM_TILES = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const OSM_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

const pinIcon = L.divIcon({
  className: "explore-map-pin",
  html: '<span class="explore-map-pin-dot"></span>',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
  popupAnchor: [0, -12],
});

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function countryBounds(country: MapCountry): L.LatLngBounds {
  return L.latLngBounds(country.bounds);
}

function focusCountry(map: L.Map, country: MapCountry): L.LatLngBounds {
  const bounds = countryBounds(country);
  const padded = bounds.pad(0.04);
  map.setMaxBounds(padded);
  const fittedZoom = map.getBoundsZoom(bounds, false);
  if (Number.isFinite(fittedZoom) && fittedZoom > 0) {
    map.setMinZoom(fittedZoom);
    map.fitBounds(bounds, {
      padding: [20, 20],
      animate: false,
      maxZoom: fittedZoom + 1,
    });
  } else {
    map.fitBounds(bounds, { padding: [20, 20], animate: false });
  }
  return bounds;
}

function pinInsideCountry(pin: MapPlacePin, bounds: L.LatLngBounds): boolean {
  return bounds.pad(0.02).contains([pin.latitude, pin.longitude]);
}

export function ExploreMap() {
  const { t } = useTranslation();
  const { language } = useLocale();
  const mapNodeRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const pinsLayerRef = useRef<L.LayerGroup | null>(null);
  const [countryId, setCountryId] = useState(MAP_COUNTRIES[0].id);
  const [mapReady, setMapReady] = useState(false);

  const selected =
    MAP_COUNTRIES.find((country) => country.id === countryId) ?? MAP_COUNTRIES[0];

  useEffect(() => {
    const node = mapNodeRef.current;
    if (!node || mapRef.current) return;

    const map = L.map(node, {
      scrollWheelZoom: false,
      attributionControl: true,
      maxBoundsViscosity: 1,
    });

    L.tileLayer(OSM_TILES, {
      attribution: OSM_ATTR,
      maxZoom: 18,
    }).addTo(map);

    mapRef.current = map;
    setMapReady(true);

    const invalidate = () => map.invalidateSize();
    const resizeObserver = new ResizeObserver(invalidate);
    resizeObserver.observe(node);
    window.addEventListener("resize", invalidate);
    requestAnimationFrame(invalidate);
    const sizeTimer = window.setTimeout(invalidate, 200);

    return () => {
      window.clearTimeout(sizeTimer);
      resizeObserver.disconnect();
      window.removeEventListener("resize", invalidate);
      map.remove();
      mapRef.current = null;
      pinsLayerRef.current = null;
      setMapReady(false);
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) return;

    if (pinsLayerRef.current) {
      map.removeLayer(pinsLayerRef.current);
      pinsLayerRef.current = null;
    }

    const pinsLayer = L.layerGroup().addTo(map);
    pinsLayerRef.current = pinsLayer;

    const bounds = focusCountry(map, selected);
    requestAnimationFrame(() => {
      map.invalidateSize();
      focusCountry(map, selected);
    });

    const controller = new AbortController();

    getMapPlaces(selected.id, language.acceptLanguageCode, controller.signal)
      .then((pins) => {
        if (controller.signal.aborted) return;
        const localPins = pins.filter((pin) => pinInsideCountry(pin, bounds));
        addPins(pinsLayer, localPins);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        if (error instanceof DOMException && error.name === "AbortError") return;
      });

    return () => {
      controller.abort();
      map.removeLayer(pinsLayer);
      if (pinsLayerRef.current === pinsLayer) pinsLayerRef.current = null;
    };
  }, [mapReady, selected, language.acceptLanguageCode]);

  return (
    <section id="explore-map" className="py-14 sm:py-20">
      <Container>
        <SectionHeading
          eyebrow={t("map.eyebrow")}
          title={t("map.title")}
          description={t("map.subtitle")}
        />

        <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
          {MAP_COUNTRIES.map((country) => {
            const active = country.id === selected.id;
            return (
              <button
                key={country.id}
                type="button"
                onClick={() => setCountryId(country.id)}
                aria-pressed={active}
                className={
                  "rounded-full border px-4 py-2 text-sm font-semibold transition-colors " +
                  (active
                    ? "border-brand-500 bg-brand-500 text-white"
                    : "border-border-soft bg-bg-base text-text-muted hover:border-brand-300 hover:text-text-heading")
                }
              >
                {t(`map.${country.key}`)}
              </button>
            );
          })}
        </div>

        <div className="mt-10 overflow-hidden rounded-2xl border border-border-soft lg:grid lg:grid-cols-2 lg:items-stretch">
          <div className="explore-map relative h-[22rem] w-full sm:h-[28rem] lg:h-full lg:min-h-[32rem]">
            <div ref={mapNodeRef} className="absolute inset-0 h-full w-full" />
          </div>

          <div className="flex flex-col justify-center bg-bg-subtle px-6 py-8 sm:px-10 sm:py-12 dark:bg-bg-muted">
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-brand-500">
              {t(`map.${selected.key}`)}
            </p>
            <p className="mt-4 text-lg leading-relaxed text-text-body sm:text-xl">
              {t(`map.${selected.key}_body`)}
            </p>
          </div>
        </div>
      </Container>
    </section>
  );
}

function addPins(layer: L.LayerGroup, pins: MapPlacePin[]): void {
  for (const pin of pins) {
    const marker = L.marker([pin.latitude, pin.longitude], { icon: pinIcon });
    const safeName = pin.name?.trim() || "Unnamed place";
    const image = pin.imageUrl
      ? `<img class="explore-map-popup-image" src="${escapeHtml(pin.imageUrl)}" alt="" loading="lazy" decoding="async" />`
      : "";
    marker.bindPopup(
      `${image}<p class="explore-map-popup-title">${escapeHtml(safeName)}</p>`,
      { className: "explore-map-popup", maxWidth: 200, minWidth: 160 },
    );
    marker.on("popupopen", () => {
      const popup = marker.getPopup();
      const popupEl = popup?.getElement();
      const img = popupEl?.querySelector<HTMLImageElement>(
        ".explore-map-popup-image",
      );
      if (!popup || !img || img.complete) return;
      const refresh = () => popup.update();
      img.addEventListener("load", refresh, { once: true });
      img.addEventListener("error", refresh, { once: true });
    });
    layer.addLayer(marker);
  }
}
